import React, { useState } from 'react';
import { categorizeWordsByTrait, WordsByTrait } from '@/utils/wordCategorization';
import { TRAIT_COLORS } from '@/lib/trait-colors';
import { Eye, EyeOff } from 'lucide-react';

interface CategorizedWordsDisplayProps {
  words: string[];
  t: (key: string) => string;
}

const TRAIT_CONFIG = [
  { key: 'dominance' as const, label: 'Drive', letter: 'D', color: TRAIT_COLORS.dominance },
  { key: 'extraversion' as const, label: 'Connection', letter: 'C', color: TRAIT_COLORS.extraversion },
  { key: 'patience' as const, label: 'Task', letter: 'T', color: TRAIT_COLORS.patience },
  { key: 'formality' as const, label: 'Order', letter: 'O', color: TRAIT_COLORS.formality },
  { key: 'objectivity' as const, label: 'Style', letter: 'S', color: TRAIT_COLORS.objectivity },
];

const CategorizedWordsDisplay: React.FC<CategorizedWordsDisplayProps> = ({ words, t }) => {
  const [showWeights, setShowWeights] = useState(true);
  const categorized = categorizeWordsByTrait(words);
  
  // Filter to only show traits that have words
  const traitsWithWords = TRAIT_CONFIG.filter(trait => categorized[trait.key].length > 0);

  if (traitsWithWords.length === 0) {
    return <p className="text-sm text-muted-foreground">No words selected</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          onClick={() => setShowWeights(prev => !prev)}
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
        >
          {showWeights ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          {showWeights ? 'Hide weights' : 'Show weights'}
        </button>
      </div>
      {traitsWithWords.map(trait => (
        <div key={trait.key}>
          <div className="flex items-center gap-2 mb-1">
            <span 
              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
              style={{ backgroundColor: trait.color }}
            >
              {trait.letter}
            </span>
            <span className="text-xs font-medium text-foreground">
              {trait.label}
            </span>
            <span className="text-xs text-muted-foreground">
              ({categorized[trait.key].length})
            </span>
          </div>
          <div className="flex flex-wrap gap-1 pl-7">
            {categorized[trait.key].map((word, idx) => {
              const directionSymbol = word.direction 
                ? (word.direction === 'positive' ? '+' : '−') 
                : '';
              return (
                <span 
                  key={idx} 
                  className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                >
                  {t(word.translationKey)}{showWeights && ` (${directionSymbol}${word.intensity})`}
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CategorizedWordsDisplay;
